import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Seat } from '@eventflow/shared-types';
import { api } from '@/lib/api';
import { supabase } from '@/lib/supabase';

type UseEventSeatsOptions = {
  enabled?: boolean;
};

type ChannelEntry = {
  channel: any;
  subscribers: number;
};

const channelRegistry = new Map<string, ChannelEntry>();

export function useEventSeats(eventId?: string, options?: UseEventSeatsOptions) {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? Boolean(eventId);

  const queryKey = useMemo(() => ['events', eventId, 'seats'] as const, [eventId]);

  const query = useQuery({
    queryKey,
    enabled: enabled && Boolean(eventId),
    queryFn: async () => {
      if (!eventId) return [] as Seat[];
      return api.get<Seat[]>(`/events/${eventId}/seats`);
    },
  });

  useEffect(() => {
    if (!enabled || !eventId) return;

    const registryKey = `seats_event_${eventId}`;
    const existing = channelRegistry.get(registryKey);

    if (existing) {
      existing.subscribers += 1;
      return () => {
        const current = channelRegistry.get(registryKey);
        if (!current) return;
        current.subscribers -= 1;
        if (current.subscribers <= 0) {
          supabase.removeChannel(current.channel);
          channelRegistry.delete(registryKey);
        }
      };
    }

    // Usamos broadcast en lugar de postgres_changes porque el backend
    // usa supabaseAdmin que no dispara eventos de postgres_changes
    const channel = supabase
      .channel(`seats:event_${eventId}`, {
        config: {
          broadcast: { self: false },
        },
      })
      .on('broadcast', { event: 'seat_update' }, (payload) => {
        const updatedSeats = payload.payload?.seats as Array<{
          id: string;
          status: string;
          locked_by_session: string | null;
          locked_until: string | null;
          seat_identifier?: string;
        }>;

        if (!updatedSeats || updatedSeats.length === 0) return;

        // Actualizar cada asiento en el cache de TanStack Query
        queryClient.setQueryData(queryKey, (prev) => {
          const current = (prev ?? []) as Seat[];
          return current.map((s) => {
            const update = updatedSeats.find((u) => u.id === s.id);
            return update ? { ...s, ...update } : s;
          });
        });
      })
      .subscribe();

    channelRegistry.set(registryKey, { channel, subscribers: 1 });

    return () => {
      const current = channelRegistry.get(registryKey);
      if (!current) return;
      current.subscribers -= 1;
      if (current.subscribers <= 0) {
        supabase.removeChannel(channel);
        channelRegistry.delete(registryKey);
      }
    };
  }, [enabled, eventId, queryClient, queryKey]);

  return query;
}
